import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import { ConfirmModal } from 'src/common/modals/ConfirmModal';
import {
  dismissDispatchModal,
  dispatcherSlice,
} from 'src/features/dispatch/dispatcher.slice';

export interface WaitAndReturnRejectModalProps {
  open: boolean;
  legTripId: string;
  siblingId: string;
  onRejectOnlyThis: () => void;
}

/**
 * Reject prompt for a single wait-and-return leg whose sibling is still
 * `requested`. Offers three choices: reject both legs (routed into the existing
 * multi-trip reject-reason flow so each leg gets a reason), reject only this leg
 * (falls through to the standard single reject flow; the backend dissolves the
 * pairing so the surviving leg becomes standalone), or cancel.
 */
export function WaitAndReturnRejectModal({
  open,
  legTripId,
  siblingId,
  onRejectOnlyThis,
}: WaitAndReturnRejectModalProps) {
  const dispatch = useAppDispatch();
  // Guards against double-submits: disables every button once a choice is made.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dismissModal = () => {
    setIsSubmitting(false);
    dispatch(dismissDispatchModal());
  };

  const onRejectBoth = () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    // Reinitialize the reject flow with both legs pre-selected so the existing
    // multi-trip reject modal collects a reason per row and bulk-rejects them.
    dispatch(
      dispatcherSlice.actions.onRejectUnassignedRequestedTrips({
        tripIdsToReject: [legTripId, siblingId],
      })
    );
  };

  const onRejectOnlyThisLeg = () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    onRejectOnlyThis();
  };

  return (
    <ConfirmModal
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) {
          dismissModal();
        }
      }}
      mode="destructive"
      icon={<ArrowPathIcon className="w-5" data-testid="wr-modal-icon" />}
      title="Reject wait & return trip?"
      description="This is one leg of a wait & return trip. How would you like to handle both legs?"
      loading={isSubmitting}
      contentClassnames="sm:max-w-[30rem]"
      onConfirm={onRejectBoth}
      confirmText="Reject both legs"
      confirmTestId="wr-reject-both"
      secondaryAction={{
        label: 'Reject only this leg',
        testId: 'wr-reject-one',
        onClick: onRejectOnlyThisLeg,
      }}
      onCancel={dismissModal}
      cancelTestId="wr-reject-cancel"
    />
  );
}
